import { buildVehicleOutline, FULL_CROP, PRINT_ARTWORK_MAX_PIXELS, PRINT_PPI_GOOD, PRINT_PPI_MIN, PRINT_VIEWS } from '@wrapforce/shared';
import type { PrintArtwork, PrintCrop, PrintPanel, PrintPlacement, PrintTemplate, PrintView } from '@wrapforce/shared';

// 'roof' is the plan (top-down) view; its label string is "Top" / "顶视图" -
// the view key stays 'roof' for data compatibility, only the displayed
// label changed. There is no separate hood view anymore (folded into 'roof').
export const PRINT_VIEW_LABEL_KEYS: Record<PrintView, string> = {
  driver_side: 'printWraps:views.driverSide',
  passenger_side: 'printWraps:views.passengerSide',
  front: 'printWraps:views.front',
  rear: 'printWraps:views.rear',
  roof: 'printWraps:views.roof',
};

// Best-effort split of a combined "2023 Tesla Model 3" label back into
// year/make/model - mirrors VehiclePicker's internal parseVehicleLabel
// (not exported from there), needed here to fill createPrintDesignSchema's
// separate fields from a VehiclePicker selection.
export function parseVehicleLabel(label: string): { year: number | null; make: string; model: string } {
  const m = label.trim().match(/^(\d{4})\s+(\S+)\s*(.*)$/);
  if (m) return { year: Number(m[1]), make: m[2] ?? '', model: m[3] ?? '' };
  const [make = '', ...rest] = label.trim().split(/\s+/);
  return { year: null, make, model: rest.join(' ') };
}

export function panelsForView(template: PrintTemplate, view: PrintView): PrintPanel[] {
  return template.panels.filter((p) => p.view === view);
}

// PRINT_VIEWS order, limited to views that have at least one placement -
// drives the AI render's layoutMediaIds and the spec PDF's per-view list.
export function viewsWithPlacements(placements: PrintPlacement[]): PrintView[] {
  const present = new Set(placements.map((p) => p.view));
  return PRINT_VIEWS.filter((v) => present.has(v));
}

const SHOW_WINDOWS_STORAGE_KEY = 'wf.printWraps.showWindows';

// Per-browser "show windows" preference for the placement editor's canvas -
// defaults to ON, and never throws (a private window, blocked storage, or
// SSR-ish environment just falls back to the default on every call).
export function readShowWindowsPreference(): boolean {
  try {
    const raw = localStorage.getItem(SHOW_WINDOWS_STORAGE_KEY);
    return raw === null ? true : raw === '1';
  } catch {
    return true;
  }
}

export function writeShowWindowsPreference(value: boolean): void {
  try {
    localStorage.setItem(SHOW_WINDOWS_STORAGE_KEY, value ? '1' : '0');
  } catch {
    // ignore - the choice just won't be remembered next time
  }
}

export function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image_load_failed'));
    img.src = url;
  });
}

export function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('canvas_to_blob_failed'));
    }, 'image/png');
  });
}

// ---------------------------------------------------------------------------
// Layer geometry helpers - shared by the toolbar (PlacementEditor) and the
// canvas's own drag math (PrintCanvas).
// ---------------------------------------------------------------------------

export function rotatePoint(x: number, y: number, deg: number): { x: number; y: number } {
  const rad = (deg * Math.PI) / 180;
  return { x: x * Math.cos(rad) - y * Math.sin(rad), y: x * Math.sin(rad) + y * Math.cos(rad) };
}

// Photoshop-style corner resize, driven by the raw pointer position in the
// view's world coordinates:
// - Free by default: width and height change independently.
// - aspectLocked (Shift): scales both dimensions together.
// - fromCenter (Alt/Option): the layer's center stays put and both edges
//   move together, instead of anchoring the opposite corner.
// `start` is the layer as it was at the start of the drag gesture - every
// call during a single drag passes the same `start`, so toggling the
// modifier keys mid-drag re-derives cleanly from that fixed reference
// instead of drifting from the previous frame's output.
export function computeLayerResize(params: {
  start: PrintPlacement;
  corner: { sx: -1 | 1; sy: -1 | 1 };
  pointerWorld: { x: number; y: number };
  aspectLocked: boolean;
  fromCenter: boolean;
  minSizeIn?: number;
}): PrintPlacement {
  const { start, corner, pointerWorld, aspectLocked, fromCenter, minSizeIn = 4 } = params;
  const centerWorld = { x: start.x + start.w / 2, y: start.y + start.h / 2 };
  const cornerOffset = rotatePoint((-corner.sx * start.w) / 2, (-corner.sy * start.h) / 2, start.rotationDeg);
  const referenceWorld = fromCenter ? centerWorld : { x: centerWorld.x + cornerOffset.x, y: centerWorld.y + cornerOffset.y };

  const local = rotatePoint(pointerWorld.x - referenceWorld.x, pointerWorld.y - referenceWorld.y, -start.rotationDeg);
  const factor = fromCenter ? 2 : 1;
  let newW = Math.max(minSizeIn, factor * corner.sx * local.x);
  let newH = Math.max(minSizeIn, factor * corner.sy * local.y);
  if (aspectLocked && start.w > 0 && start.h > 0) {
    const scale = Math.max(newW / start.w, newH / start.h);
    newW = start.w * scale;
    newH = start.h * scale;
  }

  const newCenter = fromCenter
    ? centerWorld
    : (() => {
        const rotatedHalf = rotatePoint((corner.sx * newW) / 2, (corner.sy * newH) / 2, start.rotationDeg);
        return { x: referenceWorld.x + rotatedHalf.x, y: referenceWorld.y + rotatedHalf.y };
      })();

  return { ...start, w: newW, h: newH, x: newCenter.x - newW / 2, y: newCenter.y - newH / 2 };
}

export function createLayerForArtwork(params: {
  view: PrintView;
  artworkMediaId: string;
  artworkAspect: number | null;
  viewWidthIn: number;
  viewHeightIn: number;
}): PrintPlacement {
  const { view, artworkMediaId, artworkAspect, viewWidthIn, viewHeightIn } = params;
  const aspect = artworkAspect && artworkAspect > 0 ? artworkAspect : 1;
  let w = viewWidthIn * 0.6;
  let h = w / aspect;
  if (h > viewHeightIn) {
    h = viewHeightIn;
    w = h * aspect;
  }
  return {
    id: crypto.randomUUID().slice(0, 12),
    artworkMediaId,
    view,
    x: (viewWidthIn - w) / 2,
    y: (viewHeightIn - h) / 2,
    w,
    h,
    rotationDeg: 0,
    flipX: false,
    flipY: false,
    crop: FULL_CROP,
  };
}

// Scales (keeping aspect) so the artwork covers the box, then centers it -
// the box may be a whole view or a single panel.
function coverBox(boxX: number, boxY: number, boxW: number, boxH: number, aspect: number): { x: number; y: number; w: number; h: number } {
  const scale = Math.max(boxW / aspect, boxH);
  const w = aspect * scale;
  const h = scale;
  return { x: boxX + (boxW - w) / 2, y: boxY + (boxH - h) / 2, w, h };
}

export function fitLayerToView(layer: PrintPlacement, viewWidthIn: number, viewHeightIn: number): PrintPlacement {
  const aspect = layer.w / layer.h || 1;
  const box = coverBox(0, 0, viewWidthIn, viewHeightIn, aspect);
  return { ...layer, ...box, rotationDeg: 0 };
}

// Fits to the panel's bounding box (the rectangle cut from the vinyl roll),
// even when the panel has a true polygon outline - the layer footprint is
// still a plain rect.
export function fitLayerToPanel(layer: PrintPlacement, panel: PrintPanel): PrintPlacement {
  const aspect = layer.w / layer.h || 1;
  const box = coverBox(panel.x, panel.y, panel.w, panel.h, aspect);
  return { ...layer, ...box, rotationDeg: 0 };
}

// ---------------------------------------------------------------------------
// Panel shape helpers - a panel's true outline is its `points` polygon (in
// the view's own inches) when the template provides one, otherwise its
// bounding box rectangle (x/y/w/h always equal the polygon's bbox, which is
// the rectangle cut from the vinyl roll). Every consumer that treats a panel
// as a SHAPE (the SVG clip/outline in PrintCanvas, the canvas 2D clip/
// outline in helpers.ts, and the bleed-growing math) reads this, so the SVG
// and canvas code share one shape definition.
// ---------------------------------------------------------------------------

export function panelPoints(panel: PrintPanel): [number, number][] {
  if (panel.points && panel.points.length >= 3) return panel.points;
  return [
    [panel.x, panel.y],
    [panel.x + panel.w, panel.y],
    [panel.x + panel.w, panel.y + panel.h],
    [panel.x, panel.y + panel.h],
  ];
}

// SVG path `d` for the panel's closed outline.
export function panelPath(panel: PrintPanel): string {
  const points = panelPoints(panel);
  return `M ${points.map(([x, y]) => `${x} ${y}`).join(' L ')} Z`;
}

// Shoelace-formula area of the panel's true shape (in square inches) - used
// to rank panels largest-to-smallest, e.g. for the label collision pass.
export function panelArea(panel: PrintPanel): number {
  const points = panelPoints(panel);
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const [x1, y1] = points[i]!;
    const [x2, y2] = points[(i + 1) % points.length]!;
    sum += x1 * y2 - x2 * y1;
  }
  return Math.abs(sum) / 2;
}

// Traces the panel's outline into the canvas 2D current path (in inches,
// scaled by pxPerInch) - the caller fills/strokes/clips it.
export function tracePanel(ctx: CanvasRenderingContext2D, panel: PrintPanel, pxPerInch = 1): void {
  const points = panelPoints(panel);
  points.forEach(([x, y], i) => {
    if (i === 0) ctx.moveTo(x * pxPerInch, y * pxPerInch);
    else ctx.lineTo(x * pxPerInch, y * pxPerInch);
  });
  ctx.closePath();
}

function lineIntersection(
  a0: [number, number], a1: [number, number], b0: [number, number], b1: [number, number],
): [number, number] | null {
  const dax = a1[0] - a0[0];
  const day = a1[1] - a0[1];
  const dbx = b1[0] - b0[0];
  const dby = b1[1] - b0[1];
  const denom = dax * dby - day * dbx;
  if (Math.abs(denom) < 1e-9) return null;
  const t = ((b0[0] - a0[0]) * dby - (b0[1] - a0[1]) * dbx) / denom;
  return [a0[0] + t * dax, a0[1] + t * day];
}

// Grows a simple polygon outward by `distance` inches (a mitered offset of
// each edge, then re-intersecting consecutive offset edges) - used to add a
// print bleed allowance around a panel's true shape. 0 distance or a
// degenerate (fewer than 3 point) input returns the points unchanged.
export function growPolygon(points: [number, number][], distance: number): [number, number][] {
  const n = points.length;
  if (n < 3 || distance === 0) return points;
  const centroid = points.reduce<[number, number]>((acc, p) => [acc[0] + p[0] / n, acc[1] + p[1] / n], [0, 0]);
  const offsetEdges = points.map((p0, i) => {
    const p1 = points[(i + 1) % n]!;
    const dx = p1[0] - p0[0];
    const dy = p1[1] - p0[1];
    const len = Math.hypot(dx, dy) || 1;
    let nx = -dy / len;
    let ny = dx / len;
    const midX = (p0[0] + p1[0]) / 2;
    const midY = (p0[1] + p1[1]) / 2;
    if (nx * (midX - centroid[0]) + ny * (midY - centroid[1]) < 0) {
      nx = -nx;
      ny = -ny;
    }
    return {
      p0: [p0[0] + nx * distance, p0[1] + ny * distance] as [number, number],
      p1: [p1[0] + nx * distance, p1[1] + ny * distance] as [number, number],
    };
  });
  return offsetEdges.map((cur, i) => {
    const prev = offsetEdges[(i - 1 + n) % n]!;
    return lineIntersection(prev.p0, prev.p1, cur.p0, cur.p1) ?? cur.p0;
  });
}

function pointInPolygon(x: number, y: number, points: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const [xi, yi] = points[i]!;
    const [xj, yj] = points[j]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function distanceToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  const t = lenSq > 0 ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq)) : 0;
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

function distanceToPolygonEdges(x: number, y: number, points: [number, number][]): number {
  let min = Infinity;
  for (let i = 0; i < points.length; i++) {
    const [ax, ay] = points[i]!;
    const [bx, by] = points[(i + 1) % points.length]!;
    min = Math.min(min, distanceToSegment(x, y, ax, ay, bx, by));
  }
  return min;
}

// Approximates the polygon's "pole of inaccessibility" - the interior point
// farthest from every edge - by grid-sampling its bounding box and keeping
// the sampled interior point with the largest clearance. Robust for
// concave shapes where the plain vertex centroid can fall outside the
// polygon entirely (e.g. a crescent). Falls back to the panel's bbox
// rectangle the same way panelPoints does. `maxWidth` is the clearance
// diameter (2x the distance to the nearest edge) - a simple, cheap stand-in
// for "how wide a label can be here" that a caller can shrink text to fit.
export function panelLabelAnchor(panel: PrintPanel): { x: number; y: number; maxWidth: number } {
  const points = panelPoints(panel);
  const xs = points.map((p) => p[0]);
  const ys = points.map((p) => p[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  const steps = 32;

  let bestDist = -Infinity;
  const samples: Array<{ x: number; y: number; dist: number }> = [];
  for (let i = 0; i <= steps; i++) {
    const x = minX + ((maxX - minX) * i) / steps;
    for (let j = 0; j <= steps; j++) {
      const y = minY + ((maxY - minY) * j) / steps;
      if (!pointInPolygon(x, y, points)) continue;
      const dist = distanceToPolygonEdges(x, y, points);
      samples.push({ x, y, dist });
      if (dist > bestDist) bestDist = dist;
    }
  }
  if (bestDist === -Infinity) {
    // No sampled point landed inside (a degenerate/zero-area polygon) - the
    // bbox center is the least-wrong fallback.
    return { x: centerX, y: centerY, maxWidth: Math.max(maxX - minX, 0) };
  }

  // Several sampled points can tie for the best clearance (e.g. the whole
  // central line of a plain rectangle) - among those, prefer the one
  // closest to the bbox center, so a simple rectangle anchors exactly in
  // the middle instead of an arbitrary point along the tied line.
  const tieEps = Math.max(bestDist * 1e-3, 1e-6);
  let anchorX = centerX;
  let anchorY = centerY;
  let bestCenterDist = Infinity;
  for (const s of samples) {
    if (s.dist < bestDist - tieEps) continue;
    const centerDist = Math.hypot(s.x - centerX, s.y - centerY);
    if (centerDist < bestCenterDist) {
      bestCenterDist = centerDist;
      anchorX = s.x;
      anchorY = s.y;
    }
  }
  return { x: anchorX, y: anchorY, maxWidth: bestDist * 2 };
}

export function centerLayerInView(layer: PrintPlacement, viewWidthIn: number, viewHeightIn: number): PrintPlacement {
  return { ...layer, x: (viewWidthIn - layer.w) / 2, y: (viewHeightIn - layer.h) / 2 };
}

// ---------------------------------------------------------------------------
// Camera (pan/zoom) helpers - shared by the placement editor's Photoshop-like
// pasteboard viewport (PrintCanvas). A camera maps document inches to screen
// pixels as screenPx = pan + inches * scale (scale is px per inch); pan is in
// screen pixels, not inches.
// ---------------------------------------------------------------------------

export type Camera = { scale: number; panX: number; panY: number };

export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

export function screenToInches(camera: Camera, screenX: number, screenY: number): { x: number; y: number } {
  return { x: (screenX - camera.panX) / camera.scale, y: (screenY - camera.panY) / camera.scale };
}

// Fits the document (widthIn x heightIn) centered in a containerW x containerH
// viewport, leaving `marginPx` of pasteboard on each side.
export function computeFitCamera(containerW: number, containerH: number, widthIn: number, heightIn: number, marginPx = 24): Camera {
  const availW = Math.max(1, containerW - marginPx * 2);
  const availH = Math.max(1, containerH - marginPx * 2);
  const scale = Math.max(0.01, Math.min(availW / Math.max(widthIn, 0.01), availH / Math.max(heightIn, 0.01)));
  return { scale, panX: (containerW - widthIn * scale) / 2, panY: (containerH - heightIn * scale) / 2 };
}

// Rescales the camera to `nextScale` while keeping the document point under
// (pointerX, pointerY) fixed on screen - the standard "zoom at cursor" math.
export function computeZoomAtPoint(camera: Camera, pointerX: number, pointerY: number, nextScale: number): Camera {
  const at = screenToInches(camera, pointerX, pointerY);
  return { scale: nextScale, panX: pointerX - at.x * nextScale, panY: pointerY - at.y * nextScale };
}

// Finds the snap target (from `targets`, in inches) nearest to any of the
// moving object's candidate edges/center (`values`, in inches) within
// `thresholdIn` - returns the delta to add to the object's position to align
// exactly, and the guide line position to draw. Returns null when nothing is
// within range. Used independently for the x and y axes.
export function nearestSnap(values: number[], targets: number[], thresholdIn: number): { delta: number; guide: number } | null {
  let best: { delta: number; guide: number } | null = null;
  for (const v of values) {
    for (const t of targets) {
      const delta = t - v;
      if (Math.abs(delta) <= thresholdIn && (!best || Math.abs(delta) < Math.abs(best.delta))) {
        best = { delta, guide: t };
      }
    }
  }
  return best;
}

// Smart-guide snap for a layer being dragged (move gesture only): snaps its
// left/center/right edges to the document's own edges/centerline and to
// every other layer's left/center/right (and same for top/center/bottom on
// y), independently per axis. `others` should exclude the layer being moved.
export function computeMoveSnap(params: {
  x: number; y: number; w: number; h: number;
  others: PrintPlacement[];
  viewWidthIn: number; viewHeightIn: number;
  thresholdIn: number;
}): { x: number; y: number; guideX: number | null; guideY: number | null } {
  const { x, y, w, h, others, viewWidthIn, viewHeightIn, thresholdIn } = params;
  const targetsX = [0, viewWidthIn / 2, viewWidthIn, ...others.flatMap((o) => [o.x, o.x + o.w / 2, o.x + o.w])];
  const targetsY = [0, viewHeightIn / 2, viewHeightIn, ...others.flatMap((o) => [o.y, o.y + o.h / 2, o.y + o.h])];
  const snapX = nearestSnap([x, x + w / 2, x + w], targetsX, thresholdIn);
  const snapY = nearestSnap([y, y + h / 2, y + h], targetsY, thresholdIn);
  return {
    x: snapX ? x + snapX.delta : x,
    y: snapY ? y + snapY.delta : y,
    guideX: snapX ? snapX.guide : null,
    guideY: snapY ? snapY.guide : null,
  };
}

// ---------------------------------------------------------------------------
// Ruler helpers, shared by PrintCanvas's on-screen Photoshop-style rulers
// and cropPanelArtwork's downloaded-panel ruler overlay. A tick's `pos` is a
// pixel coordinate (via the camera's screenPx = pan + valueIn * scale
// mapping, or pan = 0 for a plain fixed-scale export image); `major` ticks
// get a label, minor ticks are unlabeled subdivisions.
// ---------------------------------------------------------------------------

export type RulerTick = { pos: number; valueIn: number; major: boolean };

const RULER_STEP_CANDIDATES_IN = [1, 2, 6, 12, 24, 48];

// Picks the smallest candidate step (in inches) whose on-screen spacing at
// `scale` (px per inch) is at least `targetPx`, so major tick labels land
// roughly targetPx apart regardless of zoom. Falls back to the largest
// candidate when even that isn't enough (very zoomed out).
export function chooseRulerStep(scale: number, targetPx = 90, candidates: number[] = RULER_STEP_CANDIDATES_IN): number {
  for (const step of candidates) {
    if (step * Math.max(scale, 1e-6) >= targetPx) return step;
  }
  return candidates[candidates.length - 1]!;
}

// Computes ruler ticks (major and minor, one step beyond each edge) covering
// the visible pixel range [0, viewportPx) along one axis, via
// screenPx = pan + valueIn * scale. minorDivisions subdivides each major
// step.
export function computeRulerTicks(params: {
  scale: number;
  pan: number;
  viewportPx: number;
  minorDivisions?: number;
}): RulerTick[] {
  const { scale, pan, viewportPx, minorDivisions = 4 } = params;
  if (scale <= 0) return [];
  const majorStep = chooseRulerStep(scale);
  const minorStep = majorStep / minorDivisions;
  const startIn = (0 - pan) / scale;
  const endIn = (viewportPx - pan) / scale;
  const firstIdx = Math.floor(startIn / minorStep) - 1;
  const lastIdx = Math.ceil(endIn / minorStep) + 1;
  const ticks: RulerTick[] = [];
  for (let i = firstIdx; i <= lastIdx; i++) {
    const valueIn = i * minorStep;
    const pos = pan + valueIn * scale;
    const nearestMajorIdx = Math.round(valueIn / majorStep);
    const major = Math.abs(valueIn - nearestMajorIdx * majorStep) < minorStep * 1e-3;
    ticks.push({ pos, valueIn, major });
  }
  return ticks;
}

// Wraps into (-180, 180] the way the placement schema requires.
export function rotate90(deg: number): number {
  let next = deg + 90;
  while (next > 180) next -= 360;
  while (next <= -180) next += 360;
  return next;
}

export function clampCrop(crop: PrintCrop): PrintCrop {
  const w = Math.min(1, Math.max(0.02, crop.w));
  const h = Math.min(1, Math.max(0.02, crop.h));
  const x = Math.min(1 - w, Math.max(0, crop.x));
  const y = Math.min(1 - h, Math.max(0, crop.y));
  return { x, y, w, h };
}

// Mirrors driver-side layers onto the passenger side: position mirrors in x
// and rotation flips sign, but the artwork content itself (flipX/flipY,
// crop) is left as-is - this replaces every existing passenger-side layer.
export function mirrorLayersToPassenger(driverLayers: PrintPlacement[], viewWidthIn: number): PrintPlacement[] {
  return driverLayers.map((layer) => ({
    ...layer,
    id: crypto.randomUUID().slice(0, 12),
    view: 'passenger_side',
    x: viewWidthIn - layer.x - layer.w,
    rotationDeg: -layer.rotationDeg,
  }));
}

// ---------------------------------------------------------------------------
// AI-drawn outline compositing - the loaded image/mask/lines layers for one
// view (see @wrapforce/shared print/outline.ts's outlineViewFor), shared by
// the canvas rasterizers below and by PrintCanvas's SVG rendering. `mirror`
// says whether this view reuses the driver_side drawing flipped horizontally
// (passenger_side) - the flip is always across the view's own vertical
// centerline (x = widthIn / 2), identically in both the canvas 2D code here
// and PrintCanvas's SVG transform, so the two renderings never drift apart.
// ---------------------------------------------------------------------------

export type OutlineLayerImages = {
  image: HTMLImageElement;
  mask: HTMLImageElement;
  lines: HTMLImageElement;
  mirror: boolean;
};

// Draws `img` scaled to (widthIn, heightIn) at the canvas's current origin,
// mirrored horizontally across that box's centerline when `mirror` is true.
// pxPerInch scales the box; the caller is responsible for any translation.
function drawOutlineLayer(ctx: CanvasRenderingContext2D, img: HTMLImageElement, widthIn: number, heightIn: number, pxPerInch: number, mirror: boolean) {
  const w = widthIn * pxPerInch;
  const h = heightIn * pxPerInch;
  ctx.save();
  if (mirror) {
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
  }
  ctx.drawImage(img, 0, 0, w, h);
  ctx.restore();
}

// Renders `layers` unclipped into an offscreen canvas the size of the view,
// then masks them to the outline's printable area (globalCompositeOperation
// 'destination-in' against the mirrored mask layer) - the masked artwork
// canvas is returned for the caller to composite and/or sample.
async function renderMaskedArtwork(params: {
  layers: PrintPlacement[];
  images: Map<string, HTMLImageElement>;
  widthIn: number;
  heightIn: number;
  pxPerInch: number;
  outline: OutlineLayerImages;
}): Promise<HTMLCanvasElement> {
  const { layers, images, widthIn, heightIn, pxPerInch, outline } = params;
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(widthIn * pxPerInch));
  canvas.height = Math.max(1, Math.round(heightIn * pxPerInch));
  const ctx = canvas.getContext('2d')!;
  await renderLayers({ layers, images, pxPerInch, clipPolygons: [], ctx });
  ctx.globalCompositeOperation = 'destination-in';
  drawOutlineLayer(ctx, outline.mask, widthIn, heightIn, pxPerInch, outline.mirror);
  ctx.globalCompositeOperation = 'source-over';
  return canvas;
}

// ---------------------------------------------------------------------------
// Canvas rasterization - shared drawing core for the AI render's per-view
// layout PNGs and the per-panel print-ready crops. Draw order and the crop/
// flip/rotation math here MUST match PrintCanvas's SVG rendering exactly, or
// what the shop sees in the editor will not match what gets printed/rendered.
// ---------------------------------------------------------------------------

async function loadImagesForLayers(layers: PrintPlacement[], artworkUrls: Record<string, string>): Promise<Map<string, HTMLImageElement>> {
  const ids = Array.from(new Set(layers.map((l) => l.artworkMediaId).filter((id): id is string => !!id)));
  const map = new Map<string, HTMLImageElement>();
  await Promise.all(
    ids.map(async (id) => {
      const url = artworkUrls[id];
      if (!url) return;
      map.set(id, await loadImage(url));
    }),
  );
  return map;
}

function drawLayer(ctx: CanvasRenderingContext2D, layer: PrintPlacement, img: HTMLImageElement, pxPerInch: number) {
  const cx = (layer.x + layer.w / 2) * pxPerInch;
  const cy = (layer.y + layer.h / 2) * pxPerInch;
  const w = layer.w * pxPerInch;
  const h = layer.h * pxPerInch;
  const natW = img.naturalWidth || 1;
  const natH = img.naturalHeight || 1;
  const sx = layer.crop.x * natW;
  const sy = layer.crop.y * natH;
  const sw = layer.crop.w * natW;
  const sh = layer.crop.h * natH;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate((layer.rotationDeg * Math.PI) / 180);
  ctx.scale(layer.flipX ? -1 : 1, layer.flipY ? -1 : 1);
  ctx.drawImage(img, sx, sy, sw, sh, -w / 2, -h / 2, w, h);
  ctx.restore();
}

function drawVehicleOutline(ctx: CanvasRenderingContext2D, template: PrintTemplate, view: PrintView, pxPerInch: number) {
  const outline = buildVehicleOutline(template, view);
  ctx.save();
  ctx.scale(pxPerInch, pxPerInch);
  ctx.lineWidth = 0.06;
  ctx.fillStyle = '#F3F4F6';
  ctx.strokeStyle = '#9CA3AF';
  ctx.fill(new Path2D(outline.body));
  ctx.stroke(new Path2D(outline.body));
  ctx.fillStyle = '#DBEAFE';
  ctx.strokeStyle = '#93C5FD';
  for (const glass of outline.glass) {
    ctx.fill(new Path2D(glass));
    ctx.stroke(new Path2D(glass));
  }
  ctx.fillStyle = '#374151';
  for (const wheel of outline.wheels) {
    ctx.beginPath();
    ctx.arc(wheel.cx, wheel.cy, wheel.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

// A front view's lights are all headlights, a rear view's are all tail
// lights; a side view returns exactly [headlight, taillight] in that order
// (see buildSideLights in packages/shared). Shared by PrintCanvas's SVG
// rendering and the canvas rasterizer below so both color lights the same
// way.
export function isTailLightFeature(view: PrintView, index: number): boolean {
  if (view === 'front') return false;
  if (view === 'rear') return true;
  return index === 1;
}

// Draws lights, trim (grille/plate) and tyres - exclusion zones like glass,
// but always ON TOP of the artwork (unlike drawVehicleOutline's glass/
// wheels, which stay under it here, matching what rasterizeViewLayout has
// always drawn).
function drawExclusionFeaturesOnTop(ctx: CanvasRenderingContext2D, template: PrintTemplate, view: PrintView, pxPerInch: number, includeLightsAndTrim = true) {
  const outline = buildVehicleOutline(template, view);
  ctx.save();
  ctx.scale(pxPerInch, pxPerInch);
  ctx.lineWidth = 0.04;
  if (includeLightsAndTrim) outline.lights.forEach((d, i) => {
    const isTailLight = isTailLightFeature(view, i);
    ctx.fillStyle = isTailLight ? '#FEE2E2' : '#FEF3C7';
    ctx.strokeStyle = isTailLight ? '#EF4444' : '#F59E0B';
    const path = new Path2D(d);
    ctx.fill(path);
    ctx.stroke(path);
  });
  if (includeLightsAndTrim) {
    ctx.fillStyle = '#374151';
    ctx.globalAlpha = 0.85;
    for (const d of outline.trim) ctx.fill(new Path2D(d));
    ctx.globalAlpha = 1;
  }
  ctx.fillStyle = '#1F2937';
  for (const tyre of outline.tyres) {
    ctx.beginPath();
    ctx.roundRect(tyre.x, tyre.y, tyre.w, tyre.h, tyre.r);
    ctx.fill();
  }
  ctx.restore();
}

async function renderLayers(params: {
  layers: PrintPlacement[];
  images: Map<string, HTMLImageElement>;
  pxPerInch: number;
  // Each entry is a closed polygon (inches) to clip against - the union of
  // every entry forms the clip region. Pass panelPoints(panel) (or
  // growPolygon(panelPoints(panel), bleedIn) for a bleed allowance) per
  // panel; an empty array means no clipping.
  clipPolygons: [number, number][][];
  ctx: CanvasRenderingContext2D;
}) {
  const { layers, images, pxPerInch, clipPolygons, ctx } = params;
  ctx.save();
  if (clipPolygons.length > 0) {
    ctx.beginPath();
    for (const points of clipPolygons) {
      points.forEach(([x, y], i) => {
        if (i === 0) ctx.moveTo(x * pxPerInch, y * pxPerInch);
        else ctx.lineTo(x * pxPerInch, y * pxPerInch);
      });
      ctx.closePath();
    }
    ctx.clip();
  }
  for (const layer of layers) {
    const img = layer.artworkMediaId ? images.get(layer.artworkMediaId) : undefined;
    if (img) drawLayer(ctx, layer, img, pxPerInch);
  }
  ctx.restore();
}

// Rasterizes one view (vehicle outline + every layer clipped to the view's
// panels + panel outlines) at a target long-edge pixel size, for the
// print_layout upload the AI render prompt references. No ghosts, no
// labels, no grid.
export async function rasterizeViewLayout(params: {
  template: PrintTemplate;
  view: PrintView;
  placements: PrintPlacement[];
  artworkUrls: Record<string, string>;
  targetLongEdgePx?: number;
  // AI render inputs leave out the light and trim guide shapes: the image
  // model copies flat guide boxes literally, and the artwork is already
  // trimmed around those areas, so it draws the real parts itself.
  forAi?: boolean;
  // When set, the canvas is sized to this frame (in inches) instead of the
  // view's own dims, and the view is drawn centered horizontally and
  // bottom-aligned within it, padded with white. Used only by the render-set
  // path (collectSetLayouts) so front/rear share the side view's aspect
  // ratio and scale instead of following their own near-square dims. Every
  // other caller (spec PDF layouts, customer-photo mode) omits it and keeps
  // the previous behaviour exactly.
  frameIn?: { widthIn: number; heightIn: number };
  // The AI-drawn outline layers for this view (see OutlineLayerImages above),
  // when its outline is ready - replaces the parametric vehicle drawing and
  // panel clipping with the outline's own image/mask/lines layers, exactly
  // like PrintCanvas does. Omitted for the roof view or before the outline is
  // ready, which keeps the parametric rendering unchanged.
  outline?: OutlineLayerImages;
}): Promise<Blob> {
  const { template, view, placements, artworkUrls, targetLongEdgePx = 1500, forAi = false, frameIn, outline } = params;
  const dims = template.views[view];
  if (!dims) throw new Error('missing_view_dims');
  const layers = placements.filter((p) => p.view === view);
  const panels = panelsForView(template, view);
  const canvasWidthIn = frameIn ? frameIn.widthIn : dims.widthIn;
  const canvasHeightIn = frameIn ? frameIn.heightIn : dims.heightIn;
  const pxPerInch = targetLongEdgePx / Math.max(canvasWidthIn, canvasHeightIn);
  const images = await loadImagesForLayers(layers, artworkUrls);

  // Center the view horizontally and bottom-align it in the frame, with a
  // small margin off the very bottom edge when there's vertical padding to
  // spare (there is none, and these are both 0, when frameIn is omitted or
  // matches the view's own dims exactly).
  const offsetXIn = (canvasWidthIn - dims.widthIn) / 2;
  const verticalPadIn = canvasHeightIn - dims.heightIn;
  const offsetYIn = verticalPadIn - verticalPadIn * 0.05;

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(canvasWidthIn * pxPerInch);
  canvas.height = Math.round(canvasHeightIn * pxPerInch);
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.save();
  ctx.translate(offsetXIn * pxPerInch, offsetYIn * pxPerInch);
  if (outline) {
    drawOutlineLayer(ctx, outline.image, dims.widthIn, dims.heightIn, pxPerInch, outline.mirror);
    const masked = await renderMaskedArtwork({ layers, images, widthIn: dims.widthIn, heightIn: dims.heightIn, pxPerInch, outline });
    ctx.drawImage(masked, 0, 0);
    drawOutlineLayer(ctx, outline.lines, dims.widthIn, dims.heightIn, pxPerInch, outline.mirror);
  } else {
    drawVehicleOutline(ctx, template, view, pxPerInch);
    await renderLayers({ layers, images, pxPerInch, clipPolygons: panels.map(panelPoints), ctx });
    drawExclusionFeaturesOnTop(ctx, template, view, pxPerInch, !forAi);
  }
  ctx.strokeStyle = '#111111';
  ctx.lineWidth = Math.max(1, pxPerInch / 40);
  for (const panel of panels) {
    ctx.beginPath();
    tracePanel(ctx, panel, pxPerInch);
    ctx.stroke();
  }
  ctx.restore();
  return canvasToPngBlob(canvas);
}

// Fraction of `mask` entries that are true AND whose matching `alpha` value
// exceeds the opacity threshold (32) - the pure counting core of
// measurePanelCoverage, pulled out so it can be unit tested without a real
// canvas (jsdom has none). Both arrays are the same length, indexed the same
// way; a `mask` entry of false means "outside the panel's true polygon" and
// is skipped entirely (it isn't counted as either covered or uncovered).
export function coverageFromAlpha(alpha: ArrayLike<number>, mask: boolean[]): number {
  let inside = 0;
  let covered = 0;
  for (let i = 0; i < mask.length; i++) {
    if (!mask[i]) continue;
    inside++;
    if ((alpha[i] ?? 0) > 32) covered++;
  }
  return inside > 0 ? covered / inside : 0;
}

// Measures, per panel of one view, what fraction of that panel's true
// polygon is actually covered by placed artwork (alpha > 32) - rasterizes
// the view's layers (clipped to the panel union, same as rasterizeViewLayout)
// at a coarse pxPerInch, then samples each panel's bounding-box pixels
// against its polygon. The 'mirror' panel is skipped - it's a tiny accent
// piece, not a body panel the render prompt should reason about.
export async function measurePanelCoverage(p: {
  template: PrintTemplate;
  view: PrintView;
  placements: PrintPlacement[];
  artworkUrls: Record<string, string>;
  pxPerInch?: number;
  // See rasterizeViewLayout's `outline` param - when present, coverage for
  // the body panel is the fraction of the outline's mask area (not the
  // panel's own polygon) that placed artwork actually covers.
  outline?: OutlineLayerImages;
}): Promise<{ key: string; coveredFraction: number }[]> {
  const { template, view, placements, artworkUrls, pxPerInch = 20, outline } = p;
  const dims = template.views[view];
  const panels = panelsForView(template, view).filter((panel) => panel.key !== 'mirror');
  if (!dims || panels.length === 0) return [];
  const layers = placements.filter((l) => l.view === view);
  const images = await loadImagesForLayers(layers, artworkUrls);

  if (outline) {
    const masked = await renderMaskedArtwork({ layers, images, widthIn: dims.widthIn, heightIn: dims.heightIn, pxPerInch, outline });
    const mctx = masked.getContext('2d')!;
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = masked.width;
    maskCanvas.height = masked.height;
    const maskCtx = maskCanvas.getContext('2d')!;
    drawOutlineLayer(maskCtx, outline.mask, dims.widthIn, dims.heightIn, pxPerInch, outline.mirror);

    return panels.map((panel) => {
      const minX = Math.max(0, Math.floor(panel.x * pxPerInch));
      const minY = Math.max(0, Math.floor(panel.y * pxPerInch));
      const w = Math.max(1, Math.min(masked.width - minX, Math.ceil(panel.w * pxPerInch)));
      const h = Math.max(1, Math.min(masked.height - minY, Math.ceil(panel.h * pxPerInch)));
      const artworkData = mctx.getImageData(minX, minY, w, h).data;
      const maskData = maskCtx.getImageData(minX, minY, w, h).data;
      const mask: boolean[] = new Array(w * h);
      const alpha: number[] = new Array(w * h);
      for (let i = 0; i < w * h; i++) {
        mask[i] = (maskData[i * 4 + 3] ?? 0) > 32;
        alpha[i] = artworkData[i * 4 + 3] ?? 0;
      }
      return { key: panel.key, coveredFraction: coverageFromAlpha(alpha, mask) };
    });
  }

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(dims.widthIn * pxPerInch));
  canvas.height = Math.max(1, Math.round(dims.heightIn * pxPerInch));
  const ctx = canvas.getContext('2d')!;
  await renderLayers({ layers, images, pxPerInch, clipPolygons: panels.map(panelPoints), ctx });

  return panels.map((panel) => {
    const points = panelPoints(panel);
    const minX = Math.max(0, Math.floor(panel.x * pxPerInch));
    const minY = Math.max(0, Math.floor(panel.y * pxPerInch));
    const w = Math.max(1, Math.min(canvas.width - minX, Math.ceil(panel.w * pxPerInch)));
    const h = Math.max(1, Math.min(canvas.height - minY, Math.ceil(panel.h * pxPerInch)));
    const imageData = ctx.getImageData(minX, minY, w, h);
    const mask: boolean[] = new Array(w * h);
    const alpha: number[] = new Array(w * h);
    for (let row = 0; row < h; row++) {
      for (let col = 0; col < w; col++) {
        const idx = row * w + col;
        const inchX = (minX + col + 0.5) / pxPerInch;
        const inchY = (minY + row + 0.5) / pxPerInch;
        mask[idx] = pointInPolygon(inchX, inchY, points);
        alpha[idx] = imageData.data[idx * 4 + 3] ?? 0;
      }
    }
    return { key: panel.key, coveredFraction: coverageFromAlpha(alpha, mask) };
  });
}

// Rotation-aware bounding box of a layer, in the view's own inches - used by
// panelEffectivePpi to test whether a (possibly rotated) layer actually
// overlaps a panel before counting it toward that panel's resolution.
function layerBoundingBox(layer: PrintPlacement): { minX: number; maxX: number; minY: number; maxY: number } {
  const cx = layer.x + layer.w / 2;
  const cy = layer.y + layer.h / 2;
  const corners = ([[-1, -1], [1, -1], [1, 1], [-1, 1]] as const).map(([sx, sy]) =>
    rotatePoint((sx * layer.w) / 2, (sy * layer.h) / 2, layer.rotationDeg),
  );
  const xs = corners.map(({ x }) => cx + x);
  const ys = corners.map(({ y }) => cy + y);
  return { minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) };
}

// Three cases the UI needs to tell apart for a panel: nothing placed on it
// yet (or nothing with a known pixel size / not vector); only vector
// artwork overlapping it, which prints sharp at any size; or raster
// artwork, with the effective ppi number for the softest spot.
export type PanelArtworkQuality = { kind: 'none' } | { kind: 'vector' } | { kind: 'raster'; ppi: number };

// Effective print quality for one panel, i.e. the softest spot a shop would
// actually print. Considers every layer on the panel's view whose
// (rotation-aware) bounding box overlaps the panel's own bounding box:
// vector artwork (isVector) is ignored for the ppi number since it's never
// the blurry part, but still marks the panel as having vector coverage. A
// raster layer's ppi is its source artwork's cropped pixel size divided by
// the printed inches it's been stretched to; a smaller crop or a bigger
// printed size both lower it. When both raster and vector layers overlap,
// the raster ppi wins (it's still the softest spot). Returns 'none' when
// nothing overlaps the panel, or when the only overlapping raster artwork
// has no known pixel dimensions.
export function panelEffectivePpi(panel: PrintPanel, placements: PrintPlacement[], artworks: PrintArtwork[]): PanelArtworkQuality {
  let bestPpi: number | null = null;
  let overlappingRaster = false;
  let overlappingVector = false;
  for (const layer of placements) {
    if (layer.view !== panel.view || !layer.artworkMediaId || layer.w <= 0 || layer.h <= 0) continue;
    const artwork = artworks.find((a) => a.id === layer.artworkMediaId);
    if (!artwork) continue;
    const bbox = layerBoundingBox(layer);
    if (bbox.maxX <= panel.x || bbox.minX >= panel.x + panel.w || bbox.maxY <= panel.y || bbox.minY >= panel.y + panel.h) continue;
    if (artwork.isVector) {
      overlappingVector = true;
      continue;
    }
    if (artwork.width == null || artwork.height == null) continue;
    overlappingRaster = true;
    const ppi = Math.min((artwork.width * layer.crop.w) / layer.w, (artwork.height * layer.crop.h) / layer.h);
    if (bestPpi === null || ppi < bestPpi) bestPpi = ppi;
  }
  if (overlappingRaster && bestPpi !== null) return { kind: 'raster', ppi: Math.round(bestPpi) };
  if (overlappingVector) return { kind: 'vector' };
  return { kind: 'none' };
}

function roundUpTo100(n: number): number {
  return Math.max(100, Math.ceil(n / 100) * 100);
}

function roundDownTo100(n: number): number {
  return Math.max(100, Math.floor(n / 100) * 100);
}

// What to tell a shop owner about fixing the panel's softest (worst-ppi)
// raster layer:
// - 'bigger-image': a raster image big enough to hit PRINT_PPI_GOOD exists
//   under the upload's pixel-count limit; widthPx/heightPx is that size.
// - 'bigger-image-limited': the biggest allowed upload only reaches
//   somewhere between PRINT_PPI_MIN and PRINT_PPI_GOOD; widthPx/heightPx is
//   the largest size the app accepts.
// - 'vector-only': even the biggest allowed upload would stay under
//   PRINT_PPI_MIN, so no raster image can fix it - only vector artwork can.
export type PanelFixAdvice =
  | { kind: 'bigger-image'; widthPx: number; heightPx: number }
  | { kind: 'bigger-image-limited'; widthPx: number; heightPx: number }
  | { kind: 'vector-only' };

// Finds the worst (lowest-ppi) raster layer overlapping the panel - the same
// one that sets panelEffectivePpi's 'raster' number - and works out what
// artwork size would fix it. Returns null when no raster layer overlaps the
// panel (nothing to advise on: either empty, or vector-only already sharp).
export function panelFixAdvice(panel: PrintPanel, placements: PrintPlacement[], artworks: PrintArtwork[]): PanelFixAdvice | null {
  let worst: { layer: PrintPlacement; ppi: number } | null = null;
  for (const layer of placements) {
    if (layer.view !== panel.view || !layer.artworkMediaId || layer.w <= 0 || layer.h <= 0) continue;
    const artwork = artworks.find((a) => a.id === layer.artworkMediaId);
    if (!artwork || artwork.isVector || artwork.width == null || artwork.height == null) continue;
    const bbox = layerBoundingBox(layer);
    if (bbox.maxX <= panel.x || bbox.minX >= panel.x + panel.w || bbox.maxY <= panel.y || bbox.minY >= panel.y + panel.h) continue;
    const ppi = Math.min((artwork.width * layer.crop.w) / layer.w, (artwork.height * layer.crop.h) / layer.h);
    if (worst === null || ppi < worst.ppi) worst = { layer, ppi };
  }
  if (!worst) return null;
  const { layer } = worst;

  // Artwork pixel size (before any crop) that would make this layer's crop
  // print at exactly PRINT_PPI_GOOD, keeping the layer's own aspect ratio.
  const needW = Math.ceil((layer.w * PRINT_PPI_GOOD) / layer.crop.w);
  const needH = Math.ceil((layer.h * PRINT_PPI_GOOD) / layer.crop.h);

  if (needW * needH <= PRINT_ARTWORK_MAX_PIXELS) {
    return { kind: 'bigger-image', widthPx: roundUpTo100(needW), heightPx: roundUpTo100(needH) };
  }

  // Too big to upload at full sharpness - scale both dimensions down by the
  // same factor to land on the limit, which scales the ppi it would reach
  // by that same factor (needW/needH were sized to hit exactly GOOD ppi).
  const scale = Math.sqrt(PRINT_ARTWORK_MAX_PIXELS / (needW * needH));
  const reachedPpi = PRINT_PPI_GOOD * scale;
  if (reachedPpi < PRINT_PPI_MIN) return { kind: 'vector-only' };

  return {
    kind: 'bigger-image-limited',
    widthPx: roundDownTo100(Math.floor(needW * scale)),
    heightPx: roundDownTo100(Math.floor(needH * scale)),
  };
}
